import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Package,
  Repeat2,
  Trash2,
  Truck,
  Users,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { ROUTE } from '@/config';
import { useDeleteWorkspace, useWorkspaceDeletionSummary } from '@/hooksApi';
import { cn } from '@/lib/utils';
import { formatWithThousandSeparator } from '@/utils';
import type { Workspace } from '@/types';

/**
 * House chrome for a destructive confirmation, lifted from `ConfirmModal`'s
 * DESTRUCTIVE branch so the two cannot drift.
 *
 * ⚠️ 520px rather than its 480: this dialog carries five labelled rows with a
 * value and a status chip each, and the Arabic for "remittances in progress"
 * needs the extra 40px to stay on one line. Everything else is identical.
 */
const MODAL_STYLES = {
  content: { borderRadius: 16, padding: 24 },
  body: { padding: 0 },
  header: { padding: 0, marginBottom: 0, border: 'none' },
  footer: { border: 'none', padding: 0, marginTop: 20 },
} as const;

const BTN_CANCEL =
  'h-10 px-5 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors';

const BTN_DELETE =
  'h-10 px-5 rounded-lg border-none bg-[rgb(220,38,38)] text-white text-[13px] font-semibold transition-colors hover:bg-[rgb(185,28,28)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[rgb(220,38,38)]';

const FIELD =
  'h-[40px] w-full rounded-md border border-solid border-sd-input bg-sd-input-bg px-3 text-[14px] outline-none transition-colors focus:border-customGreen disabled:cursor-not-allowed disabled:opacity-60';

const LINK =
  'inline-flex items-center gap-1 text-[12px] font-medium text-customGreen hover:underline';

/**
 * The app's stat vocabulary, from `AdvanceCod/AdvanceStatCards` — a tinted
 * 30px chip carrying a coloured icon. Using it is the difference between a
 * designed surface and the stacked sentences the owner rejected: "you put the
 * raw text in the design and call it the UI."
 */
const IconChip = ({ children, bg }: { children: ReactNode; bg: string }) => (
  <span
    className='inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px]'
    style={{ background: bg }}
  >
    {children}
  </span>
);

/**
 * Money the way the rest of the app writes it — with one correction this
 * surface needs and the others do not.
 *
 * 🔴 ONLY THE DIGITS ARE ISOLATED, NOT THE UNIT. `AdvanceStatCards` wraps the
 * number AND the currency in one `bdi dir='ltr'`, which is fine while every
 * figure is positive. Here a balance can be NEGATIVE, and QA measured what that
 * costs in Arabic: the whole run laid out as one piece put the minus sign to
 * the RIGHT of the digits and dragged the trailing period of "د.ع." to the
 * front — "‎.د.ع 4,000-". A merchant reading that cannot tell a debt from a
 * credit, which is the one thing this row exists to say.
 *
 * Isolating the numeric part alone keeps the sign welded to the digits and
 * lets the unit sit as ordinary RTL text beside it.
 */
const Money = ({ value }: { value: number }) => {
  const { t } = useTranslation();

  return (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
      <bdi dir='ltr'>{formatWithThousandSeparator(value)}</bdi> {String(t('iqd'))}
    </span>
  );
};

interface Props {
  workspace: Workspace | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Delete a workspace, having first shown exactly what is in it.
 *
 * 🔴🔴 ONE SKELETON FOR BOTH STATES, AND THAT IS THE WHOLE REDESIGN. The
 * previous version rendered two different dialogs: the blocked one dropped the
 * figures, the explanation, the confirmation field AND the button, so
 * everything below the title moved. Owner, 2026-09-16: "It's jumping from
 * dispute to the second image ... They should be the same. You can only add and
 * remove some fields if it is removable or not."
 *
 * ⭐⭐⭐ THE FIX WAS TO STOP DUPLICATING THE NUMBERS. The old blocked state
 * printed "126 orders still in progress" in a red box — the same figure the
 * stats block above it would have shown. Marking the ROW itself collapses the
 * two states into one component whose emphasis changes and whose layout does
 * not: same five rows, same field, same buttons, every time.
 *
 * ⭐ The verdict is still the SERVER's. `can_delete` and `blockers` come from
 * the assessor the DELETE endpoint consults; nothing here re-derives it.
 */
const DeleteWorkspaceDialog = ({ workspace, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const open = Boolean(workspace);
  const {
    data,
    isLoading: isFirstLoad,
    isPreviousData,
  } = useWorkspaceDeletionSummary(workspace?.uid, open);

  /*
   * ⚠️ `isLoading` ALONE IS NOT "NOT READY". It is false whenever data is
   * present — including data belonging to the PREVIOUS workspace. The hook
   * opts out of the app-wide `keepPreviousData`, and this second condition
   * means a future change to that default cannot put one workspace's order
   * count and balance on another workspace's delete confirmation.
   */
  const isLoading = isFirstLoad || isPreviousData;
  const remove = useDeleteWorkspace();

  const [confirmation, setConfirmation] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const summary = data?.object;
  const name = workspace?.name ?? '';
  const blockers = useMemo(() => summary?.blockers ?? [], [summary]);
  const isBlocked = blockers.length > 0;

  const blockedBy = useMemo(() => new Set(blockers.map((b) => b.code)), [blockers]);

  useEffect(() => {
    if (open) setConfirmation('');
  }, [open, workspace?.uid]);

  /*
   * ⚠️ `role="alertdialog"`, SET IMPERATIVELY BECAUSE ANTD WILL NOT TAKE IT.
   * rc-dialog hard-codes `role="dialog"` and forwards only `aria-*` props — the
   * same wall that made an `aria-labelledby` prop do nothing. 2026 guidance for
   * a destructive modal is `alertdialog`, so the content is announced on open
   * rather than waiting to be explored.
   */
  useEffect(() => {
    if (!open) return;

    const id = window.setTimeout(() => {
      document
        .querySelectorAll('.ant-modal-root [role="dialog"]')
        .forEach((node) => node.setAttribute('role', 'alertdialog'));
    }, 0);

    return () => window.clearTimeout(id);
  }, [open, isLoading]);

  /** Focus goes where the only available action is. */
  useEffect(() => {
    if (!open || isLoading || !summary) return;

    const target = isBlocked ? cancelRef.current : inputRef.current;
    const id = window.setTimeout(() => target?.focus(), 0);

    return () => window.clearTimeout(id);
  }, [open, isLoading, summary, isBlocked]);

  const nameMatches = confirmation.trim() === name.trim() && name.trim().length > 0;
  const armed = Boolean(summary?.can_delete) && nameMatches && !remove.isLoading;

  /**
   * Every row, every time — the figures do not come and go. A row is marked
   * when a blocker names it, which is what ties the explanation to the number
   * instead of restating it underneath.
   */
  const rows = useMemo(() => {
    if (!summary) return [];

    return [
      {
        key: 'orders',
        icon: <Package className='h-4 w-4' style={{ color: '#2563A8' }} strokeWidth={2} />,
        bg: '#EAF0F9',
        value: summary.orders.in_progress || summary.orders.total,
        caption: summary.orders.in_progress > 0 ? 'in_progress' : 'total',
        blocking: blockedBy.has('ORDERS_IN_PROGRESS'),
      },
      {
        key: 'pickups',
        icon: <Truck className='h-4 w-4' style={{ color: '#7A5AF8' }} strokeWidth={2} />,
        bg: '#F0EDFE',
        value: summary.pickups_open,
        blocking: blockedBy.has('PICKUPS_OPEN'),
      },
      {
        key: 'remittances',
        icon: <Repeat2 className='h-4 w-4' style={{ color: '#B54708' }} strokeWidth={2} />,
        bg: '#FDF0E6',
        value: summary.remittances_pending,
        blocking: blockedBy.has('REMITTANCES_PENDING'),
      },
      {
        key: 'balance',
        icon: <Wallet className='h-4 w-4' style={{ color: '#12805C' }} strokeWidth={2} />,
        bg: '#E6F4EF',
        money: summary.cod.available,
        blocking:
          blockedBy.has('COD_AVAILABLE') ||
          blockedBy.has('COD_PENDING') ||
          blockedBy.has('BALANCE_OWED'),
      },
      {
        key: 'members',
        icon: <Users className='h-4 w-4' style={{ color: '#7A7A72' }} strokeWidth={2} />,
        bg: '#F1F1EE',
        value: summary.members.length,
        blocking: false,
      },
    ];
  }, [summary, blockedBy]);

  const showOrdersLink = blockedBy.has('ORDERS_IN_PROGRESS') || blockedBy.has('PICKUPS_OPEN');
  const showFinanceLink =
    blockedBy.has('REMITTANCES_PENDING') ||
    blockedBy.has('COD_AVAILABLE') ||
    blockedBy.has('COD_PENDING') ||
    blockedBy.has('BALANCE_OWED');

  const onDelete = () => {
    if (!workspace) return;

    remove.mutate(workspace.uid, {
      onSuccess: () => {
        toast({ title: String(t('workspace.delete_done', { name })) });
        onOpenChange(false);
      },
      onError: (err) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          String(t('workspace.delete_failed'));
        toast({ title: message, variant: 'destructive' });
      },
    });
  };

  return (
    <Modal
      open={open}
      centered
      closable={false}
      width={520}
      styles={MODAL_STYLES}
      /*
       * The heading lives in `title` because that is the only thing AntD names
       * the dialog from — measured, after passing `aria-labelledby` changed
       * nothing at all.
       */
      title={
        <div className='flex items-start gap-3'>
          <IconChip bg='#FDECEC'>
            <Trash2 className='h-4 w-4' style={{ color: 'rgb(220,38,38)' }} strokeWidth={2} />
          </IconChip>
          <div className='min-w-0'>
            <span className='block text-[17px] font-semibold leading-6 text-[rgb(57,57,60)]'>
              {String(t('workspace.delete_heading'))}
            </span>
            <bdi
              dir='auto'
              className='mt-0.5 block truncate text-[13px] font-normal text-[rgb(115,123,140)]'
            >
              {name}
            </bdi>
          </div>
        </div>
      }
      onCancel={() => onOpenChange(false)}
      footer={
        /*
         * ⚠️ CANCEL AND DELETE ARE PUSHED APART, not sat side by side. A
         * consequential action next to a benign one is a documented misclick
         * trap; the safe way out goes to the opposite end.
         */
        <div className='flex items-center justify-between gap-3'>
          <button
            ref={cancelRef}
            type='button'
            className={BTN_CANCEL}
            onClick={() => onOpenChange(false)}
          >
            {String(t('workspace.cancel'))}
          </button>
          <button type='button' className={BTN_DELETE} disabled={!armed} onClick={onDelete}>
            {remove.isLoading ? (
              <Loader2 className='me-1 inline h-3.5 w-3.5 animate-spin align-[-2px]' aria-hidden />
            ) : null}
            {String(t('workspace.delete_confirm'))}
          </button>
        </div>
      }
    >
      {isLoading || !summary ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-5 w-5 animate-spin text-[rgb(115,123,140)]' aria-hidden />
        </div>
      ) : (
        <div className='pt-4'>
          {/* THE IMPACT LIST — identical in both states. */}
          <div className='overflow-hidden rounded-2xl border border-solid border-[#EBEBE6] bg-white'>
            {rows.map((row, index) => (
              <div
                key={row.key}
                className={cn(
                  'flex items-center gap-3 px-4 py-3',
                  index > 0 && 'border-0 border-t border-solid border-[#F1F1EE]',
                  row.blocking && 'bg-[#FEF6F6]',
                )}
              >
                <IconChip bg={row.bg}>{row.icon}</IconChip>

                <span className='flex-1 text-[13px] font-semibold text-[rgb(57,57,60)]'>
                  {String(t(`workspace.row_${row.key}`))}
                </span>

                <span
                  className={cn(
                    'text-[15px] font-bold tabular-nums',
                    row.blocking ? 'text-[rgb(220,38,38)]' : 'text-[#1B1B18]',
                  )}
                >
                  {typeof row.money === 'number' ? <Money value={row.money} /> : row.value}
                </span>

                {/*
                  The chip is what replaced the red box of restated numbers. It
                  sits ON the row it is about, so the layout never changes shape
                  between a workspace that can be deleted and one that cannot.
                */}
                {row.blocking ? (
                  <span className='rounded-full bg-[rgb(220,38,38)]/10 px-2 py-0.5 text-[11px] font-semibold text-[rgb(220,38,38)]'>
                    {String(t('workspace.row_blocking'))}
                  </span>
                ) : null}
              </div>
            ))}
          </div>

          {/* THE STATUS LINE — the one element whose CONTENT changes. */}
          <div
            className={cn(
              'mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl px-3 py-2.5',
              isBlocked ? 'bg-[#FEF6F6]' : 'bg-[#EFF7F3]',
            )}
          >
            {isBlocked ? (
              <AlertTriangle
                className='h-4 w-4 shrink-0'
                style={{ color: 'rgb(220,38,38)' }}
                aria-hidden
              />
            ) : (
              <CheckCircle2 className='h-4 w-4 shrink-0' style={{ color: '#12805C' }} aria-hidden />
            )}

            <span
              className={cn(
                'flex-1 text-[13px]',
                isBlocked ? 'text-[rgb(220,38,38)]' : 'text-[#12805C]',
              )}
            >
              {String(t(isBlocked ? 'workspace.status_blocked' : 'workspace.status_ready'))}
            </span>

            {showOrdersLink ? (
              <Link to={ROUTE.ORDERS} className={LINK}>
                {String(t('workspace.delete_view_orders'))}
                <ArrowUpRight className='h-3 w-3' aria-hidden />
              </Link>
            ) : null}
            {showFinanceLink ? (
              <Link to={ROUTE.FINANCE} className={LINK}>
                {String(t('workspace.delete_view_finance'))}
                <ArrowUpRight className='h-3 w-3' aria-hidden />
              </Link>
            ) : null}
          </div>

          <p className='mb-0 mt-3 text-[12px] leading-relaxed text-[rgb(115,123,140)]'>
            {String(t('workspace.delete_explainer'))}
          </p>

          {/*
            Present in BOTH states — disabled when blocked rather than removed,
            which is what stops the dialog changing height and shape. It also
            sits BELOW the impact list on purpose: the 2026 guidance is that a
            type-to-confirm should be reached after the consequences are read.
          */}
          <label
            htmlFor='delete-confirm'
            className='mb-1.5 mt-4 block text-[13px] text-[rgb(57,57,60)]'
          >
            {String(t('workspace.delete_type_label'))}{' '}
            <bdi dir='auto' className='font-semibold'>
              {name}
            </bdi>
          </label>
          <input
            ref={inputRef}
            id='delete-confirm'
            dir='auto'
            autoComplete='off'
            spellCheck={false}
            autoCorrect='off'
            autoCapitalize='off'
            disabled={isBlocked}
            className={FIELD}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && armed) {
                e.preventDefault();
                onDelete();
              }
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default DeleteWorkspaceDialog;
